import React, { useCallback, useEffect, useRef } from 'react';
import { Formik, useFormikContext } from 'formik';

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';

const isEqual = (a, b) => {
  if (a instanceof Date && b instanceof Date) return Object.is(a.getTime(), b.getTime());
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => isEqual(item, b[index]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].every(key => isEqual(a[key], b[key]));
  }
  return Object.is(a, b);
};

const isListById = list =>
  Array.isArray(list) && list.every(item => isPlainObject(item) && item.id != null);

const isBlocLoaded = initialValues => Object.keys(initialValues || {}).length > 0;

const isAnotherFolder = (previousInitialValues, nextInitialValues) =>
  previousInitialValues?.folderId != null &&
  nextInitialValues?.folderId != null &&
  previousInitialValues.folderId !== nextInitialValues.folderId;

export const mergeUnsavedChanges = (previousInitialValues, values, nextInitialValues) => {
  if (isEqual(values, previousInitialValues)) return nextInitialValues;
  if (previousInitialValues === undefined && (values === '' || values === null))
    return nextInitialValues;

  if (
    isPlainObject(previousInitialValues) &&
    isPlainObject(values) &&
    isPlainObject(nextInitialValues)
  ) {
    return Object.keys(values).reduce(
      (merged, key) => ({
        ...merged,
        [key]: mergeUnsavedChanges(previousInitialValues[key], values[key], nextInitialValues[key]),
      }),
      nextInitialValues,
    );
  }

  if (isListById(previousInitialValues) && isListById(values) && isListById(nextInitialValues)) {
    const previousById = new Map(previousInitialValues.map(item => [item.id, item]));
    const valuesById = new Map(values.map(item => [item.id, item]));
    return nextInitialValues.map(item =>
      valuesById.has(item.id)
        ? mergeUnsavedChanges(previousById.get(item.id), valuesById.get(item.id), item)
        : item,
    );
  }

  return values;
};

const KeepUnsavedChanges = ({ initialValues, onBlocLoaded }) => {
  const formik = useFormikContext();
  const previousInitialValues = useRef(initialValues);

  useEffect(() => {
    if (isEqual(previousInitialValues.current, initialValues)) return;

    const keepsUnsavedChanges =
      isBlocLoaded(initialValues) && !isAnotherFolder(previousInitialValues.current, initialValues);
    const values = keepsUnsavedChanges
      ? mergeUnsavedChanges(previousInitialValues.current, formik.values, initialValues)
      : initialValues;
    previousInitialValues.current = initialValues;

    formik.resetForm({ values: initialValues });
    if (values !== initialValues) formik.setValues(values, false);
    if (isBlocLoaded(initialValues)) onBlocLoaded(values);
  }, [formik, initialValues, onBlocLoaded]);

  return null;
};

export const BlocFormik = ({ initialValues, onSubmit, children, ...props }) => {
  const latestOnSubmit = useRef(onSubmit);
  const submitsWaitingForBloc = useRef([]);

  latestOnSubmit.current = onSubmit;

  const handleSubmit = (values, formikHelpers) => {
    if (isBlocLoaded(initialValues)) return onSubmit(values, formikHelpers);

    return new Promise(resolve => submitsWaitingForBloc.current.push(resolve)).then(loadedValues =>
      latestOnSubmit.current(loadedValues, formikHelpers),
    );
  };

  const onBlocLoaded = useCallback(values => {
    submitsWaitingForBloc.current.forEach(resolve => resolve(values));
    submitsWaitingForBloc.current = [];
  }, []);

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} {...props}>
      {formik => (
        <>
          <KeepUnsavedChanges initialValues={initialValues} onBlocLoaded={onBlocLoaded} />
          {typeof children === 'function' ? children(formik) : children}
        </>
      )}
    </Formik>
  );
};
